import React, { useState } from 'react';
import { supabase } from '../supabaseClient';

const GRAND_TRIAL_TEMPLATES = [
  // Campus Life (5)
  { emoji: '🎓', title: 'Greek life is essential to the college experience', sideA: 'Join Greek Life', sideB: 'Stay Independent' },
  { emoji: '📚', title: 'Online degrees are just as valuable as traditional degrees', sideA: 'Online = Valid', sideB: 'Campus Required' },
  { emoji: '🏫', title: 'Small liberal arts colleges > Large state universities', sideA: 'Small College', sideB: 'Big University' },
  { emoji: '💰', title: 'Student athletes should be paid like professionals', sideA: 'Pay the Players', sideB: 'Scholarships Enough' },
  { emoji: '🎒', title: 'Taking a gap year makes you more successful', sideA: 'Gap Year Wins', sideB: 'Go Straight Through' },

  // Work Culture (8)
  { emoji: '💼', title: 'Remote work is the future of employment', sideA: 'Remote Forever', sideB: 'Office is Best' },
  { emoji: '📧', title: 'Email > Slack for workplace communication', sideA: 'Email Gang', sideB: 'Slack Squad' },
  { emoji: '⏰', title: 'Meetings before 9am should be illegal', sideA: 'Ban Early Meets', sideB: 'Morning Productive' },
  { emoji: '🏖️', title: 'Unlimited PTO policies are actually better for employees', sideA: 'Unlimited Wins', sideB: 'Set Days Better' },
  { emoji: '👔', title: 'Dress codes are outdated in modern workplaces', sideA: 'Dress Free', sideB: 'Professional Standards' },
  { emoji: '💻', title: 'Open office layouts increase productivity', sideA: 'Open > Closed', sideB: 'Need Privacy' },
  { emoji: '🎯', title: 'Side hustles are necessary in today\'s economy', sideA: 'Side Hustle Era', sideB: 'One Job Enough' },
  { emoji: '📊', title: 'Annual performance reviews should be abolished', sideA: 'Kill Reviews', sideB: 'Keep Structure' },

  // Sports GOAT Debates (5)
  { emoji: '⚽', title: 'Messi vs Ronaldo: Who is the true soccer GOAT?', sideA: 'Messi 🐐', sideB: 'Ronaldo 🐐' },
  { emoji: '🏀', title: 'Michael Jordan vs LeBron James: Basketball GOAT?', sideA: 'MJ Forever', sideB: 'Bron is King' },
  { emoji: '🎾', title: 'Federer vs Nadal vs Djokovic: Tennis GOAT?', sideA: 'Federer Grace', sideB: 'Nadal/Djokovic' },
  { emoji: '🏈', title: 'Tom Brady is overrated (he had the system)', sideA: 'Brady Overrated', sideB: 'Brady = GOAT' },
  { emoji: '⚾', title: 'Baseball needs a salary cap like other sports', sideA: 'Add Salary Cap', sideB: 'Keep It Free' },

  // Tech & Innovation (6)
  { emoji: '🤖', title: 'AI will create more jobs than it destroys', sideA: 'AI = Net Positive', sideB: 'Job Apocalypse' },
  { emoji: '🔋', title: 'Electric cars are overhyped (grid can\'t handle it)', sideA: 'EVs Overhyped', sideB: 'EVs Future' },
  { emoji: '📱', title: 'Social media has done more harm than good', sideA: 'Social Media Bad', sideB: 'Net Positive' },
  { emoji: '🎮', title: 'Video games are legitimate careers now', sideA: 'Gaming = Career', sideB: 'Just Hobby' },
  { emoji: '🔐', title: 'Privacy is dead in the digital age', sideA: 'Privacy Over', sideB: 'Can Protect It' },
  { emoji: '💻', title: 'Everyone should learn to code', sideA: 'Code for All', sideB: 'Not Necessary' },

  // Food & Lifestyle (6)
  { emoji: '🍕', title: 'Pineapple belongs on pizza (final answer)', sideA: 'Pineapple YES', sideB: 'Pineapple NO' },
  { emoji: '☕', title: 'Coffee is better than tea (objectively)', sideA: 'Coffee Wins', sideB: 'Tea Superior' },
  { emoji: '🥑', title: 'Guacamole is overrated and overpriced', sideA: 'Guac Overrated', sideB: 'Guac Worth It' },
  { emoji: '🍔', title: 'Fast food breakfast > Traditional breakfast', sideA: 'Fast Food Wins', sideB: 'Home Breakfast' },
  { emoji: '🌮', title: 'Hard shell tacos are better than soft shell', sideA: 'Hard Shell', sideB: 'Soft Shell' },
  { emoji: '🍜', title: 'Ramen is the most overrated food trend', sideA: 'Ramen Overrated', sideB: 'Ramen Elite' },
];

function GrandTrialSeeder({ authUser, onClose }) {
  const [seeding, setSeeding] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);

  const seedAllTrials = async () => {
    setSeeding(true);
    setError(null);
    setProgress({ current: 0, total: GRAND_TRIAL_TEMPLATES.length });

    try {
      for (let i = 0; i < GRAND_TRIAL_TEMPLATES.length; i++) {
        const trial = GRAND_TRIAL_TEMPLATES[i];
        
        const trialData = {
          title: trial.title,
          side_a: trial.sideA,
          side_b: trial.sideB,
          image_emoji: trial.emoji,
          trial_type: 'grand',
          created_by: authUser.id,
          creator_id: authUser.id,
          views: 0,
          shares: 0,
          votes_a: 0,
          votes_b: 0,
          status: 'active',
          jury_size: null,
          time_limit_seconds: 86400, // 24 hours
          expires_at: new Date(Date.now() + 86400 * 1000).toISOString(),
          created_at: new Date().toISOString(),
        };

        const { error: insertError } = await supabase
          .from('trials')
          .insert([trialData]);

        if (insertError) throw insertError;

        setProgress({ current: i + 1, total: GRAND_TRIAL_TEMPLATES.length });
        
        // Small delay to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setCompleted(true);
      setSeeding(false);
    } catch (err) {
      console.error('Seeding error:', err);
      setError(err.message);
      setSeeding(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🌳</div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '900' }}>
            Grand Trial Seeder
          </h2>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            Seed 30 long-form trials with 24h duration
          </p>
        </div>

        {!completed && !seeding && (
          <>
            <div style={{
              background: '#f0f9ff',
              border: '2px solid #0ea5e9',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '14px', color: '#0c4a6e', lineHeight: '1.6' }}>
                <strong>📊 What gets created:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>30 grand trials (24h duration)</li>
                  <li>5 Campus, 8 Work, 5 Sports, 6 Tech, 6 Food</li>
                  <li>Active immediately, votes accumulate over 24h</li>
                  <li>Higher-stakes debates for lasting engagement</li>
                </ul>
              </div>
            </div>

            <div style={{
              background: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '14px', color: '#78350f', lineHeight: '1.6' }}>
                <strong>⚠️ Best Practice:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Seed 1-2 days BEFORE launch</li>
                  <li>Vote on 5-10 yourself to test</li>
                  <li>Pair with QuickTrialSeeder on launch day</li>
                </ul>
              </div>
            </div>

            <button
              onClick={seedAllTrials}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '900',
                cursor: 'pointer',
                marginBottom: '12px'
              }}
            >
              🌱 Seed All 30 Grand Trials
            </button>
          </>
        )}

        {seeding && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '100%',
              height: '8px',
              background: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden',
              marginBottom: '16px'
            }}>
              <div style={{
                width: `${(progress.current / progress.total) * 100}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #7c3aed, #ec4899)',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
              Creating trial {progress.current} of {progress.total}...
            </p>
          </div>
        )}

        {completed && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '900', color: '#22c55e' }}>
              Success!
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#666' }}>
              30 grand trials created and ready for voting
            </p>
            <div style={{
              background: '#f0fdf4',
              border: '2px solid #22c55e',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              <div style={{ fontSize: '13px', color: '#166534', lineHeight: '1.6' }}>
                <strong>✓ Next Steps:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Vote on 5-10 trials to verify system works</li>
                  <li>Check Creator Dashboard to see your trials</li>
                  <li>On launch day, seed 20 quick trials for urgency</li>
                  <li>Total: 50 trials (30 lasting + 20 urgent)</li>
                </ul>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '16px',
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '900',
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          </div>
        )}

        {error && (
          <div style={{
            background: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#991b1b' }}>
              <strong>Error:</strong> {error}
            </p>
          </div>
        )}

        {!completed && (
          <button
            onClick={onClose}
            disabled={seeding}
            style={{
              width: '100%',
              padding: '12px',
              background: 'transparent',
              color: '#666',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: seeding ? 'not-allowed' : 'pointer',
              opacity: seeding ? 0.5 : 1
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export default GrandTrialSeeder;
